import React from "react";

export interface MissingCleaningRateApartment {
    id: number;
    name: string;
}

export interface MissingCleaningRatesBannerProps {
    /** Apartments that currently have no effective cleaning rate. */
    apartments: MissingCleaningRateApartment[];
    /** Invoked with an apartment id to start adding a rate for it. */
    onAdd: (apartmentId: number) => void;
}

/**
 * Warns that some apartments have no current cleaning rate and turns each into an
 * actionable entry point. Previously these apartments were a dead end: the grid
 * only lists apartments that already have a rate, so an apartment with none had
 * no row to "edit" and therefore no way to get its first rate. Each name is now a
 * button that opens the add dialog for that apartment.
 */
export const MissingCleaningRatesBanner: React.FC<MissingCleaningRatesBannerProps> = ({ apartments, onAdd }) => {
    if (apartments.length === 0) {
        return null;
    }

    return (
        <div
            role="status"
            className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800"
        >
            <span className="font-medium">No current cleaning rate:</span>
            {apartments.map((apartment) => (
                <button
                    key={apartment.id}
                    type="button"
                    onClick={() => onAdd(apartment.id)}
                    aria-label={`Add cleaning rate for ${apartment.name}`}
                    className="rounded underline decoration-dotted underline-offset-2 hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-700"
                >
                    {apartment.name}
                </button>
            ))}
        </div>
    );
};
